import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useJsApiLoader } from '@react-google-maps/api'
import PlacesAutocomplete, { geocodeByAddress } from 'react-places-autocomplete'
import { Button } from '@/components/Button/Button'
import { TextInput, ErrorBlockText } from '@/components/widget-components'
import { useCorpStore } from '@/reducers/corporateReducer'
import { ROUTES } from '../CorporateSignupPanel'
import { CountrySelector } from '@/components/Select/CountrySelector'
import { RegionSelector } from '@/components/Select/RegionSelector'
import { isValidPostalCode } from '@/components/Select/regionData'
import axiosInstance from '@/requests/axios'
import {
  toAddress,
  toPostalValue,
  toRegionValue,
  updateCorporate,
} from '@/requests/corporate'
import { useEnrollmentHydration } from '@/hooks/useEnrollmentHydration'
import clsx from 'clsx'

export function CorporateBusinessAddress() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const nextState = useCorpStore((state) => state.nextState)
  const streetAddress1 = useCorpStore((state) => state.streetAddress1)
  const setStreetAddress1 = useCorpStore((state) => state.setStreetAddress1)
  const streetAddress2 = useCorpStore((state) => state.streetAddress2)
  const setStreetAddress2 = useCorpStore((state) => state.setStreetAddress2)
  const city = useCorpStore((state) => state.city)
  const setCity = useCorpStore((state) => state.setCity)
  const country = useCorpStore((state) => state.country)
  const setCountry = useCorpStore((state) => state.setCountry)
  const province = useCorpStore((state) => state.province)
  const setProvince = useCorpStore((state) => state.setProvince)
  const postalCode = useCorpStore((state) => state.postalCode)
  const setPostalCode = useCorpStore((state) => state.setPostalCode)

  const [errorMessage, setErrorMessage] = useState('')
  const [touched, setTouched] = useState({
    streetAddress1: false,
    city: false,
    province: false,
    postalCode: false,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: `${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`,
    libraries: ['places'],
  })

  const searchOptions = useMemo(
    () => ({
      componentRestrictions: { country: country.toLowerCase() },
      types: ['address'],
    }),
    [country]
  )

  useEnrollmentHydration(
    useCallback((enrollment) => {
      const address = enrollment.address
      if (!address) return
      setStreetAddress1(address.addressLine1 ?? '')
      setStreetAddress2(address.addressLine2 ?? '')
      setCity(address.city ?? '')
      const country = (address.country ?? 'CA').toUpperCase()
      setCountry(country)
      setProvince(toRegionValue({ country, province: address.province ?? '' }))
      setPostalCode(
        toPostalValue({ country, postalCode: address.postalCode ?? '' })
      )
    }, [])
  )

  const formatPostalCode = useCallback(
    (code: string): string => {
      if (country !== 'CA') {
        return code
      }
      const cleaned = code.replace(/\s/g, '').toUpperCase()
      if (cleaned.length > 3) {
        return cleaned.slice(0, 3) + ' ' + cleaned.slice(3)
      }
      return cleaned
    },
    [country]
  )

  const validateField = useCallback(
    (field: string, value: string) => {
      switch (field) {
        case 'streetAddress1':
          return !value.trim()
            ? t('corporateBusinessAddress.errors.streetAddressRequired')
            : ''
        case 'city':
          return !value.trim()
            ? t('corporateBusinessAddress.errors.cityRequired')
            : ''
        case 'province':
          return !value.trim()
            ? t('corporateBusinessAddress.errors.provinceRequired')
            : ''
        case 'postalCode': {
          const requiresPostal = country === 'CA' || country === 'US'
          if (!requiresPostal) return ''
          if (!value.trim())
            return t('corporateBusinessAddress.errors.postalCodeRequired')
          if (!isValidPostalCode({ country, code: value }))
            return country === 'US'
              ? t('corporateBusinessAddress.errors.postalCodeInvalidUs')
              : t('corporateBusinessAddress.errors.postalCodeInvalid')
          return ''
        }
        default:
          return ''
      }
    },
    [country, t]
  )

  useEffect(() => {
    const formValues = {
      streetAddress1,
      city,
      province,
      postalCode,
    }

    const isValid = !Object.entries(formValues).some(([field, value]) =>
      validateField(field, value)
    )
    setIsFormValid(isValid)

    // Only show errors for touched fields
    if (Object.values(touched).some((t) => t)) {
      const touchedFieldErrors = Object.entries(touched)
        .filter(([, isTouched]) => isTouched)
        .map(([field]) =>
          validateField(field, formValues[field as keyof typeof formValues])
        )
        .filter((error) => error !== '')
      setErrorMessage(touchedFieldErrors[0] || '')
    } else {
      setErrorMessage('')
    }
  }, [streetAddress1, city, province, postalCode, touched, validateField])

  const handleAddressChange = (address: string) => {
    setStreetAddress1(address)
    setTouched((prev) => ({ ...prev, streetAddress1: true }))
  }

  const handleAddressSelect = (address: string) => {
    const addressObj: Record<string, string> = {}
    geocodeByAddress(address)
      .then((results) => {
        const addressComponents = results[0].address_components
        for (const component of addressComponents) {
          for (const type of component.types) {
            addressObj[type] =
              type === 'administrative_area_level_1'
                ? component.short_name
                : component.long_name
          }
        }

        setStreetAddress1(
          `${addressObj.street_number ? addressObj.street_number : ''} ${addressObj.route ? addressObj.route : ''}`
        )
        setStreetAddress2(addressObj.subpremise || '')
        setCity(addressObj.locality || '')
        const selectedProvince = addressObj.administrative_area_level_1 || ''
        setProvince(
          country === 'US' ? selectedProvince.toLowerCase() : selectedProvince
        )
        const selectedPostal = addressObj.postal_code || ''
        setPostalCode(
          country === 'CA' ? selectedPostal.replace(/\s/g, '') : selectedPostal
        )

        setTouched({
          streetAddress1: true,
          city: true,
          province: true,
          postalCode: true,
        })
      })
      .catch(() => {
        setErrorMessage(t('corporateBusinessAddress.errors.addressSelectError'))
      })
  }

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPostalCode(e.target.value)
    const maxLength = country === 'CA' ? 7 : 20
    if (formatted.length <= maxLength) {
      setPostalCode(formatted)
      setTouched((prev) => ({ ...prev, postalCode: true }))
    }
  }

  const handleCountryChange = useCallback(
    (value: string) => {
      setCountry(value)
      setProvince('')
      setPostalCode('')
      setTouched((prev) => ({ ...prev, province: false, postalCode: false }))
    },
    [setCountry, setProvince, setPostalCode]
  )

  const validateForm = useCallback(() => {
    setTouched({
      streetAddress1: true,
      city: true,
      province: true,
      postalCode: true,
    })

    const errors = {
      streetAddress1: validateField('streetAddress1', streetAddress1),
      city: validateField('city', city),
      province: validateField('province', province),
      postalCode: validateField('postalCode', postalCode),
    }

    const firstError = Object.values(errors).find((error) => error !== '')
    setErrorMessage(firstError || '')

    return !Object.values(errors).some((error) => error !== '')
  }, [streetAddress1, city, province, postalCode, validateField])

  const handleSubmit = useCallback(async () => {
    setTouched({
      streetAddress1: true,
      city: true,
      province: true,
      postalCode: true,
    })

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    try {
      const addressValues = {
        address1: streetAddress1,
        address2: streetAddress2 || '',
        city: city,
        province: province,
        postal: postalCode,
      }

      const lowerCasedValues = Object.fromEntries(
        Object.entries(addressValues).map(([key, value]) => [
          key,
          typeof value === 'string' ? value.toLowerCase() : value,
        ])
      )

      try {
        await updateCorporate({
          address: toAddress({ ...addressValues, country }),
        })
        nextState()
        navigate(ROUTES.CORPORATE_ADDITIONAL_DETAILS)
      } catch {
        setErrorMessage(t('corporateBusinessAddress.errors.updateFailed'))
      }
    } catch {
      setErrorMessage(t('corporateBusinessAddress.errors.updateFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }, [
    streetAddress1,
    streetAddress2,
    city,
    country,
    province,
    postalCode,
    validateForm,
    nextState,
    navigate,
    t,
  ])

  // Add the global keydown effect
  useEffect(() => {
    const handleGlobalKeyDown = async (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isFormValid && !isSubmitting) {
        e.preventDefault()
        // Don't trigger submit if suggestions are showing
        const suggestions = document.querySelector('.location-search-input')
          ?.nextElementSibling?.children
        if (!suggestions || suggestions.length === 0) {
          await handleSubmit()
        }
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown)

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown)
    }
  }, [isFormValid, isSubmitting, handleSubmit])

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="container-corporate-address"
    >
      <div
        className="text-lg font-normal uppercase"
        data-testid="text-corporate-address-heading"
      >
        {t('corporateBusinessAddress.title')}
      </div>

      {isLoaded ? (
        <PlacesAutocomplete
          value={streetAddress1}
          onChange={handleAddressChange}
          onSelect={handleAddressSelect}
          searchOptions={searchOptions}
        >
          {({
            getInputProps,
            suggestions,
            getSuggestionItemProps,
            loading,
          }) => (
            <div
              className="relative"
              data-testid="container-corporate-address-autocomplete"
            >
              <TextInput
                {...getInputProps({
                  placeholder: t(
                    'corporateBusinessAddress.streetAddress.placeholder'
                  ),
                  className: 'location-search-input',
                })}
                data-testid="input-corporate-address-street"
              />
              <div
                className="absolute w-full z-50"
                data-testid="container-corporate-address-suggestions"
              >
                {loading && (
                  <div
                    className="bg-white dark:bg-neutral-900 p-2 border-x border-pebble dark:border-white"
                    data-testid="container-corporate-address-loading"
                  >
                    {t('corporateBusinessAddress.loading.suggestions')}
                  </div>
                )}
                {suggestions.length > 0 && (
                  <div
                    className="bg-white dark:bg-neutral-900 border-x border-b border-pebble dark:border-white shadow-lg"
                    data-testid="container-corporate-address-suggestions-list"
                  >
                    {suggestions.map((suggestion) => {
                      const className = suggestion.active
                        ? 'bg-rock dark:bg-neutral-800 p-2 cursor-pointer'
                        : 'hover:bg-rock dark:hover:bg-neutral-800 p-2 cursor-pointer'

                      return (
                        <div
                          {...getSuggestionItemProps(suggestion, {
                            className,
                          })}
                          data-testid={`corporate-address-suggestion-${suggestion.placeId.substring(0, 8)}`}
                        >
                          <span>{suggestion.description}</span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </PlacesAutocomplete>
      ) : (
        <div data-testid="container-corporate-address-loading-maps">
          {t('corporateBusinessAddress.loading.maps')}
        </div>
      )}

      <TextInput
        placeholder={t('corporateBusinessAddress.unit.placeholder')}
        value={streetAddress2}
        onChange={(e) => setStreetAddress2(e.target.value)}
        data-testid="input-corporate-address-unit"
      />

      <TextInput
        placeholder={t('corporateBusinessAddress.city.placeholder')}
        value={city}
        onChange={(e) => {
          setCity(e.target.value)
          setTouched((prev) => ({ ...prev, city: true }))
        }}
        data-testid="input-corporate-address-city"
      />

      <CountrySelector
        country={country}
        onSelectCountry={handleCountryChange}
        data-testid="select-corporate-address-country-selector"
      />

      <div
        className="grid grid-cols-2 gap-4"
        data-testid="container-corporate-address-location-grid"
      >
        <RegionSelector
          country={country}
          region={province}
          onSelectRegion={(value) => {
            setProvince(value)
            setTouched((prev) => ({ ...prev, province: true }))
          }}
          data-testid="select-corporate-address-province-selector"
        />

        <TextInput
          placeholder={t('corporateBusinessAddress.postalCode.placeholder')}
          value={postalCode}
          maxLength={country === 'CA' ? 7 : 20}
          onChange={handlePostalCodeChange}
          data-testid="input-corporate-address-postal"
        />
      </div>

      {errorMessage && (
        <ErrorBlockText data-testid="error-corporate-address">
          {errorMessage}
        </ErrorBlockText>
      )}

      <Button
        onClick={handleSubmit}
        disabled={isSubmitting || !isFormValid}
        className={clsx(
          'mt-4 inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2',
          {
            'opacity-50 cursor-not-allowed bg-gray-400':
              !isFormValid || isSubmitting,
            'hover:bg-gray-500': !isFormValid || isSubmitting,
          }
        )}
        data-testid="btn-corporate-address-submit"
      >
        {isSubmitting
          ? t('corporateBusinessAddress.submitButton.saving')
          : t('corporateBusinessAddress.submitButton.continue')}
      </Button>
    </div>
  )
}
